import { AlertTriangle, Ban, Info } from "lucide-react"
import { cn } from "@/lib/utils"
import type { MyUsage } from "@/services/usageService"

/** TenantMonthlyBanner のプロパティ。 */
interface TenantMonthlyBannerProps {
  /** 本人スコープの利用状況。null のとき何も表示しない。 */
  usage: MyUsage | null
}

/**
 * テナント月次の利用枠バナー。
 *
 * tenantMonthly.limit が設定（>0）されている場合のみ表示する。
 * - blocked（grace 超過）: 遮断バナー（red）
 * - 100% 到達: 警告バナー（red）
 * - 80% 到達: 注意バナー（amber）
 * - それ未満: 何も表示しない
 * limit 未設定（null/0）のときは一切表示しない。
 * @param usage 本人スコープの利用状況（null なら非表示）。
 * @returns 月次バナー要素、または null。
 */
export function TenantMonthlyBanner({ usage }: TenantMonthlyBannerProps) {
  if (!usage) return null

  const { credits, limit, blocked } = usage.tenantMonthly
  // limit 未設定（null/0）= 上限なし。バナーは一切出さない。
  if (limit === null || limit <= 0) return null

  const ratio = credits / limit

  // 遮断（grace 超過）。最優先で遮断バナーを出す。
  if (blocked) {
    return (
      <Banner tone="danger" icon={<Ban className="h-4 w-4 shrink-0" />}>
        今月の利用枠を使い切りました。来月のリセット、または枠の追加をご相談ください。
      </Banner>
    )
  }

  // 100% 到達=警告バナー。
  if (ratio >= 1) {
    return (
      <Banner tone="danger" icon={<AlertTriangle className="h-4 w-4 shrink-0" />}>
        今月の利用枠（{limit} クレジット）に到達しました。猶予分を消費中です。
      </Banner>
    )
  }

  // 80% 到達=注意バナー。
  if (ratio >= 0.8) {
    return (
      <Banner tone="warning" icon={<Info className="h-4 w-4 shrink-0" />}>
        今月の利用枠の 80% を超えました（{credits} / {limit} クレジット）。
      </Banner>
    )
  }

  return null
}

/** バナーの配色トーン。 */
type BannerTone = "warning" | "danger"

/** Banner のプロパティ。 */
interface BannerProps {
  /** 配色トーン。 */
  tone: BannerTone
  /** 先頭アイコン。 */
  icon: React.ReactNode
  /** バナー本文。 */
  children: React.ReactNode
}

/**
 * トーン別の帯状バナー。
 * @param tone 配色トーン（warning=amber / danger=red）。
 * @param icon 先頭アイコン。
 * @param children バナー本文。
 * @returns バナー要素。
 */
function Banner({ tone, icon, children }: BannerProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-2 text-sm border-l-4",
        tone === "danger"
          ? "bg-red-50 border-red-500 text-red-700"
          : "bg-amber-50 border-amber-500 text-amber-700"
      )}
      role="status"
    >
      {icon}
      <span>{children}</span>
    </div>
  )
}
